import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/images/logo.png";

const NavbarPremium = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Auditions", path: "/auditions" },
    { name: "Apply", path: "/apply" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-gradient-header backdrop-blur-md shadow-kawaii border-b border-pastel-purple/20" 
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/">
              <motion.div
                className="flex items-center gap-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-pastel-purple/40 hover:border-pastel-purple transition-all duration-300 shadow-soft">
                  <img
                    src={logo}
                    alt="Livneu"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-xl font-title text-text-primary kawaii-glow">
                    Livneu
                  </h1>
                  <p className="text-xs font-accent text-text-accent">
                    The Talent Agency
                  </p>
                </div>
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8 relative">
              {navItems.map((item, idx) => (
                <Link key={item.name} to={item.path} className="relative">
                  <motion.div
                    className={`px-4 py-2 rounded-full transition-all duration-300 font-accent font-medium ${
                      isActive(item.path)
                        ? "text-text-accent bg-pastel-purple shadow-soft"
                        : "text-text-primary hover:text-text-accent hover:bg-pastel-purple/20"
                    }`}
                    whileHover={{ 
                      scale: 1.05,
                      y: -2
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item.name}
                  </motion.div>
                </Link>
              ))}

              {/* CTA Button */}
              <Link to="/apply">
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    y: -2,
                    boxShadow: "0 8px 24px rgba(200, 162, 255, 0.2)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-button hover:bg-gradient-button-hover text-black font-accent font-medium px-6 py-2 rounded-full ml-4 shadow-button transition-all duration-300"
                >
                  Join Us
                </motion.button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-text-primary hover:text-text-accent transition-colors rounded-full hover:bg-pastel-purple/20"
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`bg-current h-0.5 w-6 rounded-full transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1' : ''}`}></span>
                <span className={`bg-current h-0.5 w-6 rounded-full transition-all duration-300 my-1 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`bg-current h-0.5 w-6 rounded-full transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1' : ''}`}></span>
              </div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-card-background/95 backdrop-blur-md border-t border-pastel-purple/20 shadow-kawaii"
            >
              <div className="container mx-auto px-6 py-6">
                <div className="flex flex-col gap-4">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block px-4 py-3 rounded-full transition-all duration-300 font-accent ${
                          isActive(item.path)
                            ? "bg-gradient-button text-black shadow-button"
                            : "text-text-primary hover:text-text-accent hover:bg-pastel-purple/20"
                        }`}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navItems.length * 0.1 }}
                    className="pt-4 border-t border-pastel-purple/20"
                  >
                    <Link to="/apply" onClick={() => setIsMenuOpen(false)}>
                      <button className="w-full bg-gradient-button hover:bg-gradient-button-hover text-black font-accent font-medium px-6 py-3 rounded-full shadow-button transition-all duration-300">
                        Join Our Agency
                      </button>
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Spacer to prevent content overlap */}
      <div className="h-20"></div>
    </>
  );
};

export default NavbarPremium;
